import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { NavigateFunction } from 'react-router-dom';
import type { AppTranslations } from 'translations/translations';
import type { ShowNotificationCommand } from 'utils/hooks/useNotifications';
import type { DraftStateUpdater } from 'utils/react/StateProxy';
import type { ApiRequestFunction } from 'utils/request/useApi';
import type { WebHookValues } from '../WebHookForm';
import {
  type WebHookType,
  type WebHooksState,
  type WebhookTrigger,
  webHookTypes,
} from '../WebHooksState';
import { WEBHOOKS_BASE_URL } from '../constants';
import type { PrivateWebHooksCommands } from '../useWebHooks';

export interface SaveWebHookCommand {
  values: WebHookValues;
}

// TreePicker leaf keys are `<type>/<action>/<status>` (snapshot) or
// `<type>/<status>` (intent-verification); reconstruct the trigger conditions.
const serializeTrigger = (trigger: Set<string>): WebhookTrigger[] =>
  Array.from(trigger, (key) => {
    const [type = '', second = '', third] = key.split('/');
    return third === undefined
      ? { type: type as WebHookType, status: second }
      : { type: type as WebHookType, action: second, status: third };
  });

// Outbound auth token update semantics: clear → null; a new value → replace;
// blank → omit the key entirely (leaves the stored token unchanged).
const buildTokenPatch = (
  token: string,
  clearToken?: boolean,
): { webhookConfig?: { token: string } | null } => {
  if (clearToken) {
    return { webhookConfig: null };
  }
  if (token) {
    return { webhookConfig: { token } };
  }
  return {};
};

// Collapse the editor's ordered rows into the persisted `{ name: value }` map,
// dropping rows with a blank name.
const serializeHeaders = (
  headers: { key: string; value: string }[],
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const { key, value } of headers) {
    const name = key.trim();
    if (name) {
      out[name] = value;
    }
  }
  return out;
};

export const makeSaveWebHook =
  (
    apiRequest: ApiRequestFunction,
    msg: AppTranslations,
    showNotification: (command: ShowNotificationCommand) => void,
    navigate: NavigateFunction,
    getCommands: () => PrivateWebHooksCommands,
    updateState: DraftStateUpdater<WebHooksState>,
  ) =>
  async ({
    // `useTriggers` is a UI-only toggle; it selects which mode to persist
    // (fine-grained `trigger` vs. legacy `sendAll`/`types`), but is not sent.
    // `token`/`clearToken`/`hasToken` drive `webhookConfig` and are not sent
    // as-is (the strict API schema would reject them); `headers` is serialized
    // into `headerTemplate`. `caCertId` is normalized (empty → null) before it
    // is put back on the request body.
    values: {
      id,
      sendAll,
      trigger,
      useTriggers,
      headers,
      token,
      clearToken,
      hasToken: _hasToken,
      caCertId,
      ...values
    },
  }: SaveWebHookCommand): Promise<void> => {
    const selectedTypes = webHookTypes.filter((value) => values.types[value]);
    // Trigger mode and legacy type mode are mutually exclusive: persist exactly
    // one and clear the other so the backend never sees a conflicting config.
    // The custom CA, token, and payload/header templates are fine-grained-only,
    // so the legacy branch clears them all.
    const modeData = useTriggers
      ? {
          sendAll: false,
          types: [],
          trigger: serializeTrigger(trigger),
          payloadTemplate: values.payloadTemplate,
          headerTemplate: serializeHeaders(headers),
          caCertId: caCertId || null,
          ...buildTokenPatch(token, clearToken),
        }
      : {
          sendAll,
          types: sendAll ? [] : selectedTypes,
          trigger: [],
          payloadTemplate: null,
          headerTemplate: null,
          caCertId: null,
        };
    const data = { ...values, ...modeData };

    await withLoadingIndicator((state) => {
      updateState((draft) => {
        draft.detailLoadingState = state;
      });
    })(async () => {
      if (id) {
        await apiRequest({
          url: `/webhooks/${id}`,
          method: 'PUT',
          data,
        });
      } else {
        const response = await apiRequest({
          url: '/webhooks',
          method: 'POST',
          data,
        });

        updateState((draft) => {
          draft.savedSecret = response.secret;
        });
      }
    });

    const { loadWebHooks } = getCommands();

    loadWebHooks();
    showNotification({
      type: 'success',
      message: msg.settings.webHooks.request[id ? 'saved' : 'created'],
    });
    navigate(WEBHOOKS_BASE_URL);
  };
